import React from "react";
import { Box, Typography, Grid, IconButton, CardContent, Button } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { CustomTextField } from "../../../components/common-components/CustomTextField";
import GlobalTalentLocationPicker from "../../../components/location-picker/GlobalTalentLocationPicker";
import {
  FormCard,
  FormSection,
  SearchButtonContainer,
  StyledSearchButton,
  LoadingSearchButton,
  SkillsTooltip,
  ExperienceSlider,
} from "./style";
import { CustomStyledCircularProgress } from "../../../components/common-components/CustomStyledCircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import { colors } from "../../../theme/colors";

const GlobalTalentSearch = ({
  formik,
  isGlobalSearching,
  locationInput,
  skillInput,
  setSkillInput,
  experienceMarks,
  locationDropdownOpen,
  setLocationDropdownOpen,
  selectedCountry,
  countryOptions,
  citySearchQuery,
  setCitySearchQuery,
  loadingCities,
  filteredCities,
  handleCountrySelect,
  handleCitySelect,
  handleLocationDropdownClose,
  handleLocationRemove,
  handleLocationClear,
  handleResetSearch,
}) => {
  return (
    <FormCard>
      <CardContent className="advanced-filter-container">
        <Typography variant="h5" className="form-title" gutterBottom>
          Advanced Search
        </Typography>
        <Typography variant="body2" className="form-subtitle" gutterBottom>
          Refine your search with specific criteria to find the perfect
          candidates
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormSection>
              <Typography variant="h6" className="field-label">
                Job Title
              </Typography>
              <CustomTextField
                placeholder="e.g., Senior React Developer, Product Manager"
                fullWidth
                name="job_title"
                value={formik.values.job_title}
                onChange={formik.handleChange}
                disabled={isGlobalSearching}
                variant="outlined"
              />
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Typography variant="h6" className="field-label">
                Years of Experience
              </Typography>
              <Box sx={{ px: 1, pt: 1 }}>
                <ExperienceSlider
                  value={formik.values.experience}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("experience", newValue);
                  }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) =>
                    value >= 15 ? "15+" : `${value}`
                  }
                  min={0}
                  max={15}
                  step={1}
                  marks={experienceMarks}
                  disabled={isGlobalSearching}
                  disableSwap
                />
              </Box>
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Typography variant="h6" className="field-label">
                Location
              </Typography>
              <GlobalTalentLocationPicker
                multiple
                disabled={isGlobalSearching}
                locationInput={locationInput}
                locationDropdownOpen={locationDropdownOpen}
                setLocationDropdownOpen={setLocationDropdownOpen}
                selectedCountry={selectedCountry}
                countryOptions={countryOptions}
                citySearchQuery={citySearchQuery}
                setCitySearchQuery={setCitySearchQuery}
                loadingCities={loadingCities}
                filteredCities={filteredCities}
                handleCountrySelect={handleCountrySelect}
                handleCitySelect={handleCitySelect}
                handleLocationDropdownClose={handleLocationDropdownClose}
                handleLocationRemove={handleLocationRemove}
                handleLocationClear={handleLocationClear}
              />
            </FormSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSection>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" className="field-label">
                  Skills
                </Typography>
                <SkillsTooltip
                  title={
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Skill Input Format
                      </Typography>
                      <Typography variant="body2" paragraph>
                        You can enter skills in two formats:
                      </Typography>
                      <Typography variant="body2" component="div">
                        <strong>1. Simple skill name:</strong> React, Leadership
                        <br />
                        <strong>2. Skill with experience:</strong> Python:3+,
                        SQL:2, Azure:4-6
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="body2" fontWeight="bold">
                          Valid patterns:
                        </Typography>
                        <ul style={{ margin: "8px 0", paddingLeft: "18px" }}>
                          <li>React</li>
                          <li>Python:3</li>
                          <li>SQL:2+</li>
                          <li>Azure:1-3</li>
                        </ul>
                      </Box>
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <IconButton sx={{ marginBottom: "8px" }} size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </SkillsTooltip>
              </Box>
              <CustomTextField
                placeholder={
                  formik.values.skills.length === 0
                    ? "e.g., React:3+, Node.js, AWS"
                    : ""
                }
                fullWidth
                name="skills"
                value={skillInput}
                onChange={(e) => {
                  const input = e.target.value;
                  setSkillInput(input);
                  const parts = input
                    .split(",")
                    .map((part) => part.trim())
                    .filter((part) => part);
                  formik.setFieldValue("skills", parts);
                }}
                disabled={isGlobalSearching}
                variant="outlined"
              />
            </FormSection>
          </Grid>
        </Grid>

        <SearchButtonContainer sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          {isGlobalSearching ? (
            <LoadingSearchButton disabled>
              <span>Searching Global Talent</span>
              <CustomStyledCircularProgress size={20} />
            </LoadingSearchButton>
          ) : (
            <>
              <StyledSearchButton
                variant="contained"
                size="large"
                onClick={formik.handleSubmit}
                disabled={
                  !formik.values.job_title && !formik.values.skills.length
                }
              >
                <SearchIcon sx={{ marginRight: "8px" }} />
                Search Global Talent
              </StyledSearchButton>
              <Button
                variant="outlined"
                size="large"
                onClick={handleResetSearch}
                startIcon={<RestartAltIcon />}
                sx={{
                  borderRadius: "30px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  px: 3,
                  borderColor: colors.border.default,
                  color: "#64748b",
                  "&:hover": {
                    borderColor: colors.primary,
                    color: colors.primary,
                    backgroundColor: colors.primaryLight,
                  },
                }}
              >
                Reset Search
              </Button>
            </>
          )}
        </SearchButtonContainer>
      </CardContent>
    </FormCard>
  );
};

export default GlobalTalentSearch;
